// ──────────────────────────────────────────────────────────────
//  dashboardQueries.ts — Describes the analytics queries each
//  dashboard widget needs, so a whole page can be requested in one
//  batch instead of a request per widget.
//
//  The page builds the batch from these descriptions and each widget
//  reads its own results back by the same keys — which is why the key
//  builder and the query shapes live here rather than in either of
//  them. A widget that described its needs differently from the page
//  that fetched them would simply render empty.
// ──────────────────────────────────────────────────────────────

import type {
	AnalyticsCatalog,
	AnalyticsFilterClause,
	DashboardWidget,
	DashboardWidgetFilter
} from '../AppTypes';

/** The distinct pieces of data a single widget can need. */
export type WidgetQueryPart = 'total' | 'previous' | 'sparkline' | 'main' | 'cohort';

/** A metric query in a batch request. */
export interface BatchQuerySpec {
	Key: string;
	Metrics: string[];
	From: string;
	To: string;
	Granularity: string;
	GroupBy?: string[];
	Filters?: AnalyticsFilterClause[];
	MaxGroups?: number;
}

/** A cohort matrix in a batch request. */
export interface BatchCohortSpec {
	Key: string;
	Days: number;
}

/** Everything one page of widgets needs from the server. */
export interface BatchRequests {
	queries: BatchQuerySpec[];
	cohorts: BatchCohortSpec[];
}

/**
 * The key a widget's result is filed under. Shared by the page that asks and the
 * widget that reads.
 *
 * @param widgetId - The widget's stable id.
 * @param part     - Which piece of the widget's data this is.
 * @returns The batch key.
 */
export function widgetQueryKey(widgetId: string, part: WidgetQueryPart): string {
	return `${widgetId}:${part}`;
}

/**
 * Formats a date the way the query API expects: a local-time calendar day.
 *
 * @param date - The date to format.
 * @returns `YYYY-MM-DD`.
 */
export function toDateInputValue(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/**
 * The window a widget covers, in days, honouring a dashboard-wide override.
 *
 * @param widget        - The widget.
 * @param rangeOverride - The page override in days; 0 means use the widget's own.
 * @returns The effective window length.
 */
export function effectiveRangeDays(widget: DashboardWidget, rangeOverride: number): number {
	return rangeOverride > 0 ? rangeOverride : (widget.rangeDays ?? 90);
}

/** The date window a widget queries, plus the start of the preceding one for comparisons. */
export interface WidgetWindow {
	rangeDays: number;
	fromDate: string;
	toDate: string;
	previousFromDate: string;
}

/**
 * Resolves the date window a widget queries.
 *
 * @param widget        - The widget.
 * @param rangeOverride - The page override in days; 0 means use the widget's own.
 * @returns The window, and the start of the period before it.
 */
export function widgetWindow(widget: DashboardWidget, rangeOverride: number): WidgetWindow {
	const rangeDays = effectiveRangeDays(widget, rangeOverride);
	const now = Date.now();
	const day = 24 * 60 * 60 * 1000;
	/*
	 * The query's upper bound is exclusive (`time < to`), so tomorrow is what includes
	 * all of today; the window start shifts with it so the span stays exactly rangeDays.
	 */
	return {
		rangeDays,
		toDate: toDateInputValue(new Date(now + day)),
		fromDate: toDateInputValue(new Date(now + day - rangeDays * day)),
		previousFromDate: toDateInputValue(new Date(now + day - rangeDays * 2 * day))
	};
}

/**
 * The metric keys a widget queries — one, or several for multi, funnel and waterfall.
 *
 * @param widget - The widget.
 * @returns The metric keys, in order.
 */
export function metricKeys(widget: DashboardWidget): string[] {
	return widget.viz === 'multi' || widget.viz === 'funnel' || widget.viz === 'waterfall'
		? (widget.metrics ?? [])
		: [widget.metric];
}

/**
 * Whether every metric a widget queries declares the given field.
 *
 * @param widget   - The widget.
 * @param catalog  - The metric catalogue.
 * @param fieldKey - The field to test.
 * @returns True when the field applies to the whole widget.
 */
export function widgetDeclaresField(
	widget: DashboardWidget,
	catalog: AnalyticsCatalog | undefined,
	fieldKey: string
): boolean {
	const keys = metricKeys(widget).filter((key) => key.length > 0);
	return (
		keys.length > 0 &&
		keys.every(
			(key) => catalog?.Metrics.find((metric) => metric.Key === key)?.Fields.some((field) => field.Key === fieldKey) ?? false
		)
	);
}

/**
 * The dashboard-wide clauses that apply to a widget.
 *
 * A clause is applied only when **every** metric the widget queries declares its field.
 * The engine rejects a filter naming a field the metric does not expose, so applying one
 * anyway would fail the widget outright rather than simply leaving it unfiltered.
 *
 * @param widget  - The widget.
 * @param catalog - The metric catalogue.
 * @param filters - The page-wide clauses.
 * @returns The clauses that apply here.
 */
export function dashboardFilters(
	widget: DashboardWidget,
	catalog: AnalyticsCatalog | undefined,
	filters: DashboardWidgetFilter[]
): AnalyticsFilterClause[] {
	return (filters ?? [])
		.filter((filter) => filter.field.length > 0 && widgetDeclaresField(widget, catalog, filter.field))
		.map((filter) => ({ Field: filter.field, Operator: filter.operator, Value: filter.value }));
}

/**
 * The full filter list for a widget: its own clauses plus whichever page clauses apply.
 *
 * @param widget  - The widget.
 * @param catalog - The metric catalogue.
 * @param filters - The page-wide clauses.
 * @returns Every clause to send with this widget's queries.
 */
export function widgetFilters(
	widget: DashboardWidget,
	catalog: AnalyticsCatalog | undefined,
	filters: DashboardWidgetFilter[]
): AnalyticsFilterClause[] {
	return [
		...(widget.filters ?? []).map((filter) => ({
			Field: filter.field,
			Operator: filter.operator,
			Value: filter.value
		})),
		...dashboardFilters(widget, catalog, filters)
	];
}

/**
 * Describes everything a single widget needs fetched.
 *
 * @param widget        - The widget to describe.
 * @param catalog       - The metric catalogue, used to decide which page filters apply.
 * @param rangeOverride - A page-wide range override in days; 0 means the widget's own.
 * @param filters       - The page-wide filter clauses.
 * @returns The queries and cohort matrices this widget needs.
 */
export function widgetRequests(
	widget: DashboardWidget,
	catalog: AnalyticsCatalog | undefined,
	rangeOverride: number,
	filters: DashboardWidgetFilter[]
): BatchRequests {
	/* A section is pure layout — it has nothing to ask for. */
	if (widget.viz === 'section') return { queries: [], cohorts: [] };

	const { rangeDays, fromDate, toDate, previousFromDate } = widgetWindow(widget, rangeOverride);

	/* A cohort matrix comes from its own endpoint, not the metric engine. */
	if (widget.viz === 'cohort') {
		return { queries: [], cohorts: [{ Key: widgetQueryKey(widget.id, 'cohort'), Days: rangeDays }] };
	}

	const metrics = metricKeys(widget).filter((key) => key.length > 0);
	/* A widget whose metric was removed from the registry has nothing to ask for. */
	if (metrics.length === 0) return { queries: [], cohorts: [] };

	const clauses = widgetFilters(widget, catalog, filters);
	const queries: BatchQuerySpec[] = [];

	/* A headline value, plus optionally the preceding period and a trend series. */
	if (widget.viz === 'value' || widget.viz === 'gauge') {
		queries.push({
			Key: widgetQueryKey(widget.id, 'total'),
			Metrics: metrics,
			From: fromDate,
			To: toDate,
			Granularity: 'none',
			Filters: clauses
		});

		if (widget.compare === true) {
			queries.push({
				Key: widgetQueryKey(widget.id, 'previous'),
				Metrics: metrics,
				From: previousFromDate,
				To: fromDate,
				Granularity: 'none',
				Filters: clauses
			});
		}

		if (widget.viz === 'value' && widget.sparkline === true) {
			queries.push({
				Key: widgetQueryKey(widget.id, 'sparkline'),
				Metrics: metrics,
				From: fromDate,
				To: toDate,
				Granularity: widget.granularity ?? 'day',
				Filters: clauses
			});
		}

		return { queries, cohorts: [] };
	}

	/* A ranked table is one grouped, unbucketed query. */
	if (widget.viz === 'table') {
		queries.push({
			Key: widgetQueryKey(widget.id, 'main'),
			Metrics: metrics,
			From: fromDate,
			To: toDate,
			Granularity: 'none',
			GroupBy: widget.groupBy ?? [],
			Filters: clauses,
			MaxGroups: widget.maxGroups
		});
		return { queries, cohorts: [] };
	}

	/* A funnel and a waterfall both want one total per stage. */
	if (widget.viz === 'funnel' || widget.viz === 'waterfall') {
		queries.push({
			Key: widgetQueryKey(widget.id, 'main'),
			Metrics: metrics,
			From: fromDate,
			To: toDate,
			Granularity: 'none',
			Filters: clauses
		});
		return { queries, cohorts: [] };
	}

	queries.push({
		Key: widgetQueryKey(widget.id, 'main'),
		Metrics: metrics,
		From: fromDate,
		To: toDate,
		Granularity: widget.granularity ?? 'day',
		/* A multi-metric chart overlays its metrics as series, so it never also splits by a group. */
		GroupBy: widget.viz === 'multi' ? [] : (widget.groupBy ?? []),
		Filters: clauses,
		MaxGroups: widget.maxGroups
	});
	return { queries, cohorts: [] };
}

/**
 * Describes everything a page of widgets needs, as a single batch.
 *
 * @param widgets       - The widgets on the page, in display order.
 * @param catalog       - The metric catalogue.
 * @param rangeOverride - A page-wide range override in days; 0 means each widget's own.
 * @param filters       - The page-wide filter clauses.
 * @returns One batch covering every widget.
 */
export function pageRequests(
	widgets: DashboardWidget[],
	catalog: AnalyticsCatalog | undefined,
	rangeOverride: number,
	filters: DashboardWidgetFilter[]
): BatchRequests {
	const queries: BatchQuerySpec[] = [];
	const cohorts: BatchCohortSpec[] = [];

	/* One pass, so the batch keeps the widgets' display order. */
	for (const widget of widgets) {
		const requests = widgetRequests(widget, catalog, rangeOverride, filters);
		queries.push(...requests.queries);
		cohorts.push(...requests.cohorts);
	}

	return { queries, cohorts };
}
